import {
  HuddleStatus,
  IHuddle,
  IHuddleInvite,
  IHuddleSpeakerCheck,
  IHuddleSignalDelivery,
} from '@common/interfaces/huddles'

/** A set of listeners for one kind of event: `on` adds one and returns its unsubscribe. */
const channel = <T = void>() => {
  const listeners = new Set<(value: T) => void>()
  return {
    on: (listener: (value: T) => void): (() => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    emit: (value: T): void => listeners.forEach((listener) => listener(value)),
  }
}

/**
 * The live huddle in each conversation this tab has looked at, and the signals
 * relayed to it. Fed from the socket by a saga; read by hooks and the huddle session.
 */
class HuddleStore {
  private readonly byChannel = new Map<string, IHuddle | null>()

  private readonly changes = channel()

  private readonly signals = channel<IHuddleSignalDelivery>()

  private readonly huddles = channel<IHuddle>()

  private readonly invites = channel<IHuddleInvite>()

  private readonly speakerChecks = channel<IHuddleSpeakerCheck>()

  subscribe = (listener: () => void): (() => void) => this.changes.on(listener)

  /** Undefined until this tab has asked; null when nothing is going on there. */
  get = (channelId: string): IHuddle | null | undefined => this.byChannel.get(channelId)

  set(channelId: string, huddle: IHuddle | null): void {
    const live = huddle?.status === HuddleStatus.Live && huddle.peers.length > 0 ? huddle : null
    this.byChannel.set(channelId, live)
    this.changes.emit()
  }

  receive(huddle: IHuddle): void {
    this.set(huddle.channelId, huddle)
    this.huddles.emit(huddle)
  }

  onHuddle(listener: (huddle: IHuddle) => void): () => void {
    return this.huddles.on(listener)
  }

  receiveSignal(signal: IHuddleSignalDelivery): void {
    this.signals.emit(signal)
  }

  onSignal(listener: (signal: IHuddleSignalDelivery) => void): () => void {
    return this.signals.on(listener)
  }

  receiveInvite(invite: IHuddleInvite): void {
    this.invites.emit(invite)
  }

  receiveSpeakerCheck(check: IHuddleSpeakerCheck): void {
    this.speakerChecks.emit(check)
  }

  onSpeakerCheck(listener: (check: IHuddleSpeakerCheck) => void): () => void {
    return this.speakerChecks.on(listener)
  }

  onInvite(listener: (invite: IHuddleInvite) => void): () => void {
    return this.invites.on(listener)
  }
}

export const huddleStore = new HuddleStore()
